import { BackendContentBlock } from 'Models'
import type { JSONContent } from '@tiptap/core'

/**
 * TipTap JSON <-> backend content blocks. Pure functions, no React.
 *
 * KNOWN LIMITATION (documented, not hidden): the backend rejects HTML in
 * prose fields and does not confirm any rich-text property, so inline marks
 * (bold / italic / links inside a paragraph) are flattened to plain text on
 * save. The editor's Bold and Italic toolbar buttons therefore do not
 * persist. Re-introduce marks only after the backend confirms a rich-text
 * field.
 */

/**
 * Node/mark names the live ArticleEditor can actually render. Used on load to
 * decide which backend blocks would be silently lost by a round trip through
 * the editor. Keep in sync with the extensions in ArticleEditor.tsx; when you
 * add @tiptap/extension-image or -link, add 'image' / 'link' here.
 */
export const DEFAULT_EDITOR_CAPABILITIES: ReadonlySet<string> = new Set([
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'blockquote',
  'codeBlock',
])

const BLOCK_LEVEL_NODES = new Set([
  'paragraph',
  'heading',
  'listItem',
  'bulletList',
  'orderedList',
  'blockquote',
  'codeBlock',
])

/** Flattens a node to plain text, dropping marks. Block children are joined by newlines, hard breaks become newlines. */
const getText = (node?: JSONContent): string => {
  if (!node) return ''
  if (node.type === 'text') return node.text ?? ''
  if (node.type === 'hardBreak') return '\n'

  const children = node.content ?? []
  const separator = children.some((child) => child.type && BLOCK_LEVEL_NODES.has(child.type))
    ? '\n'
    : ''
  return children.map(getText).join(separator)
}

const getFirstLinkHref = (node: JSONContent): string | null => {
  for (const child of node.content ?? []) {
    const linkMark = child.marks?.find((mark) => mark.type === 'link')
    if (linkMark?.attrs?.href) return String(linkMark.attrs.href)
  }
  return null
}

const mapNode = (node: JSONContent): BackendContentBlock | null => {
  switch (node.type) {
    case 'paragraph': {
      const text = getText(node).trim()
      if (!text) return null // an empty paragraph has nothing to save

      // A paragraph that is entirely one link-marked run becomes a `link` block.
      if (node.content?.length === 1) {
        const href = getFirstLinkHref(node)
        if (href) return { type: 'link', data: { text, url: href } }
      }
      return { type: 'paragraph', data: { text } }
    }

    case 'heading': {
      const text = getText(node).trim()
      if (!text) return null
      const level = Math.min(6, Math.max(1, Number(node.attrs?.level ?? 2)))
      return { type: 'header', data: { text, level } }
    }

    case 'bulletList':
    case 'orderedList': {
      // Nested lists are flattened into the parent item as extra lines; the
      // backend list block is flat.
      const items = (node.content ?? []).map((item) => getText(item).trim()).filter(Boolean)
      if (!items.length) return null
      return {
        type: 'list',
        data: { style: node.type === 'orderedList' ? 'ordered' : 'unordered', items },
      }
    }

    case 'blockquote': {
      // Keep every paragraph inside the quote (joined by newlines), not just the first.
      const text = getText(node).trim()
      if (!text) return null
      // No toolbar control produces a caption yet; add `caption` here when one exists.
      return { type: 'quote', data: { text } }
    }

    case 'codeBlock': {
      const code = getText(node).replace(/\s+$/, '') // keep leading indentation
      if (!code) return null
      return { type: 'code', data: { code } }
    }

    case 'image': {
      const url = node.attrs?.src as string | undefined
      if (!url) return null
      return {
        type: 'image',
        data: {
          file: { url },
          alt: (node.attrs?.alt as string) ?? '',
          ...(node.attrs?.title ? { caption: String(node.attrs.title) } : {}),
        },
      }
    }

    default:
      return null
  }
}

/** Top-level TipTap node types this adapter knows how to convert (or knowingly drop when empty). */
const KNOWN_TOP_LEVEL = new Set([
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'blockquote',
  'codeBlock',
  'image',
])

export interface TiptapConversionResult {
  blocks: BackendContentBlock[]
  /** Node types the adapter could not map (e.g. horizontalRule). Saving must be blocked or the admin warned: never drop these silently. */
  unsupportedNodeTypes: string[]
}

/**
 * Converts a TipTap document to backend blocks. Takes the doc's `content`
 * array, never the `{ type: 'doc' }` wrapper.
 */
export const tiptapToBackend = (document: JSONContent): TiptapConversionResult => {
  const unsupportedNodeTypes: string[] = []
  const blocks: BackendContentBlock[] = []

  for (const node of document.content ?? []) {
    const block = mapNode(node)
    if (block) {
      blocks.push(block)
    } else if (node.type && !KNOWN_TOP_LEVEL.has(node.type) && !unsupportedNodeTypes.includes(node.type)) {
      unsupportedNodeTypes.push(node.type)
    }
  }

  return { blocks, unsupportedNodeTypes }
}

// ---------------------------------------------------------------------------
// Backend limits (integration guide): 200 blocks, 100,000 serialized chars,
// 20,000 chars per text/code block, lists up to 100 items of 2,000 chars.
// ---------------------------------------------------------------------------

const MAX_BLOCKS = 200
const MAX_SERIALIZED = 100_000
const MAX_TEXT = 20_000
const MAX_LIST_ITEMS = 100
const MAX_LIST_ITEM_LENGTH = 2_000

/** Returns human-readable problems with a block array. Empty array means it is within the documented limits. */
export const validateBlocks = (blocks: BackendContentBlock[]): string[] => {
  const issues: string[] = []

  if (blocks.length > MAX_BLOCKS) issues.push(`Content has ${blocks.length} blocks; the limit is ${MAX_BLOCKS}.`)
  if (JSON.stringify(blocks).length > MAX_SERIALIZED) issues.push('Content is too long to save (over 100,000 characters).')

  blocks.forEach((block, index) => {
    const position = index + 1
    const { data } = block
    if (typeof data.text === 'string' && data.text.length > MAX_TEXT) {
      issues.push(`Block ${position} is over ${MAX_TEXT.toLocaleString()} characters.`)
    }
    if (typeof data.code === 'string' && data.code.length > MAX_TEXT) {
      issues.push(`Code block ${position} is over ${MAX_TEXT.toLocaleString()} characters.`)
    }
    if (Array.isArray(data.items)) {
      if (data.items.length > MAX_LIST_ITEMS) issues.push(`List ${position} has more than ${MAX_LIST_ITEMS} items.`)
      if (data.items.some((item) => item.length > MAX_LIST_ITEM_LENGTH)) {
        issues.push(`List ${position} has an item over ${MAX_LIST_ITEM_LENGTH.toLocaleString()} characters.`)
      }
    }
  })

  return issues
}

// ---------------------------------------------------------------------------
// Reverse: backend blocks -> TipTap JSON
// ---------------------------------------------------------------------------

/** Plain text to TipTap inline nodes; newlines become hard breaks. */
const textNodes = (text: string): JSONContent[] => {
  if (!text) return []
  const nodes: JSONContent[] = []
  text.split('\n').forEach((line, index) => {
    if (index > 0) nodes.push({ type: 'hardBreak' })
    if (line) nodes.push({ type: 'text', text: line })
  })
  return nodes
}

/** The editor capability a block needs in order to survive a round trip. Empty string means the block type is unknown. */
const requiredCapability = (block: BackendContentBlock): string => {
  switch (block.type) {
    case 'paragraph':
      return 'paragraph'
    case 'header':
      return 'heading'
    case 'quote':
      return 'blockquote'
    case 'code':
      return 'codeBlock'
    case 'image':
      return 'image'
    case 'link':
      return 'link'
    case 'list':
      return block.data.style === 'ordered' ? 'orderedList' : 'bulletList'
    default:
      return ''
  }
}

const backendBlockToTiptap = (block: BackendContentBlock): JSONContent | null => {
  const { data } = block

  switch (block.type) {
    case 'paragraph':
      return { type: 'paragraph', content: textNodes(data.text ?? '') }

    case 'header':
      return {
        type: 'heading',
        attrs: { level: Number(data.level) || 2 },
        content: textNodes(data.text ?? ''),
      }

    case 'quote':
      return {
        type: 'blockquote',
        content: (data.text ?? '')
          .split('\n')
          .map((line) => ({ type: 'paragraph', content: textNodes(line) })),
      }

    case 'list': {
      const items = Array.isArray(data.items) ? data.items : []
      return {
        type: data.style === 'ordered' ? 'orderedList' : 'bulletList',
        content: items.map((item) => ({
          type: 'listItem',
          content: [{ type: 'paragraph', content: textNodes(item) }],
        })),
      }
    }

    case 'code':
      return { type: 'codeBlock', content: data.code ? [{ type: 'text', text: data.code }] : [] }

    case 'image':
      if (!data.file?.url) return null
      return {
        type: 'image',
        attrs: {
          src: data.file.url,
          alt: data.alt ?? '',
          ...(data.caption ? { title: data.caption } : {}),
        },
      }

    case 'link':
      if (!data.url) return null
      return {
        type: 'paragraph',
        content: [
          {
            type: 'text',
            text: data.text ?? data.url,
            marks: [{ type: 'link', attrs: { href: data.url } }],
          },
        ],
      }

    default:
      return null
  }
}

export interface BackendConversionResult {
  document: JSONContent
  /**
   * Backend block types that CANNOT be shown in this editor (unknown custom
   * types, or known types whose TipTap extension is not installed, such as
   * image/link today). Saving the article from this editor would delete them,
   * so the caller must warn the admin and pause saving until acknowledged.
   */
  droppedBlockTypes: string[]
}

export const backendToTiptap = (
  blocks: BackendContentBlock[] = [],
  capabilities: ReadonlySet<string> = DEFAULT_EDITOR_CAPABILITIES,
): BackendConversionResult => {
  const droppedBlockTypes: string[] = []
  const content: JSONContent[] = []

  for (const block of blocks) {
    const capability = requiredCapability(block)
    const supported = capability !== '' && capabilities.has(capability)
    const node = supported ? backendBlockToTiptap(block) : null

    if (node) {
      content.push(node)
    } else if (!droppedBlockTypes.includes(block.type)) {
      droppedBlockTypes.push(block.type)
    }
  }

  return { document: { type: 'doc', content }, droppedBlockTypes }
}
